import type {
  AgentRuntimeInteractionRequest,
  AgentRuntimeQuestionPrompt,
  AgentRuntimeStatus,
  AgentType,
} from "@cord/shared"
import { normalizeAgentErrorDetails, normalizeClaudeAskUserInput } from "@cord/shared"

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null
  return value as Record<string, unknown>
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null
}

function asReplicationIntegrity(
  value: unknown,
): AgentRuntimeStatus["replicationIntegrity"] {
  const row = asRecord(value)
  const state = asString(row?.state)
  if (state !== "healthy" && state !== "suspect" && state !== "broken") {
    return null
  }

  return {
    state,
    reason: asString(row?.reason),
    detectedAt: normalizeTimestamp(row?.detectedAt),
  }
}

function asRuntimeFinality(value: unknown): AgentRuntimeStatus["runtimeFinality"] {
  const row = asRecord(value)
  const state = asString(row?.state)
  if (state !== "live" && state !== "settled" && state !== "unknown") {
    return null
  }

  return {
    state,
    reason: asString(row?.reason),
  }
}

function normalizeTimestamp(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) return null
  const normalized = Math.floor(value)
  return normalized < 10_000_000_000 ? normalized * 1000 : normalized
}

function normalizeQuestionPrompts(raw: Record<string, unknown>, requestId: string) {
  const sourcePrompts = Array.isArray(raw.questions)
    ? raw.questions
    : Array.isArray(raw.prompts)
      ? raw.prompts
      : []

  const prompts = sourcePrompts
    .map((entry, index) => {
      const prompt = asRecord(entry)
      if (!prompt) return null
      const options = Array.isArray(prompt.options)
        ? prompt.options
            .map((option, optionIndex) => {
              const row = asRecord(option)
              const label = asString(row?.label) ?? asString(row?.name)
              if (!label) return null
              return {
                id: asString(row?.id) ?? `${requestId}:${index}:${optionIndex}`,
                label,
                description: asString(row?.description) ?? undefined,
              }
            })
            .filter((option): option is NonNullable<typeof option> => option !== null)
        : []
      return {
        id: asString(prompt.id) ?? `${requestId}:${index}`,
        prompt: asString(prompt.prompt) ?? asString(prompt.question) ?? "",
        header: asString(prompt.header) ?? asString(prompt.title) ?? undefined,
        allowMultiple: prompt.allowMultiple === true || prompt.multiple === true,
        allowCustom: prompt.allowCustom === true || prompt.custom === true,
        options,
      }
    })
    .filter((prompt): prompt is NonNullable<typeof prompt> => prompt !== null)

  if (prompts.length > 0) return prompts

  const fallbackPrompt = asString(raw.prompt) ?? asString(raw.title) ?? "Choose an option"
  const fallbackOptions = Array.isArray(raw.options)
    ? raw.options
        .map((option, index) => {
          const row = asRecord(option)
          const label = asString(row?.label) ?? asString(row?.name)
          if (!label) return null
          return {
            id: asString(row?.id) ?? `${requestId}:fallback:${index}`,
            label,
            description: asString(row?.description) ?? undefined,
          }
        })
        .filter((option): option is NonNullable<typeof option> => option !== null)
    : []

  return [
    {
      id: `${requestId}:fallback`,
      prompt: fallbackPrompt,
      allowMultiple: false,
      allowCustom: false,
      options: fallbackOptions,
    },
  ]
}

function buildPlanPrompts(raw: Record<string, unknown>, requestId: string) {
  const title = asString(raw.title) ?? asString(raw.prompt) ?? "Approve the generated plan?"
  const options = Array.isArray(raw.options)
    ? raw.options
        .map((option, index) => {
          const row = asRecord(option)
          const label = asString(row?.label) ?? asString(row?.name)
          if (!label) return null
          return {
            id: asString(row?.id) ?? `${requestId}:plan:${index}`,
            label,
            description: asString(row?.description) ?? undefined,
          }
        })
        .filter((option): option is NonNullable<typeof option> => option !== null)
    : [
        { id: `${requestId}:approve`, label: "Approve", description: "Proceed with the plan" },
        { id: `${requestId}:reject`, label: "Request changes", description: "Send the plan back for revision" },
      ]

  return {
    title,
    prompts: [
      {
        id: `${requestId}:plan`,
        prompt: title,
        allowMultiple: false,
        allowCustom: false,
        options,
      },
    ],
  }
}

function sameInteractionProviderRef(
  args: {
    left: AgentRuntimeInteractionRequest["providerRef"]
    right: AgentRuntimeInteractionRequest["providerRef"]
    compareMessageToolLinkage?: boolean
  },
): boolean {
  const compareMessageToolLinkage = args.compareMessageToolLinkage !== false

  return (
    args.left?.agentType === args.right?.agentType &&
    args.left?.sessionId === args.right?.sessionId &&
    (!compareMessageToolLinkage || (
      args.left?.messageId === args.right?.messageId &&
      args.left?.toolCallId === args.right?.toolCallId
    ))
  )
}

function sameQuestionPrompts(
  args: {
    left: AgentRuntimeQuestionPrompt[]
    right: AgentRuntimeQuestionPrompt[]
    compareIds: boolean
  },
): boolean {
  if (args.left.length !== args.right.length) return false

  for (let promptIndex = 0; promptIndex < args.left.length; promptIndex += 1) {
    const leftPrompt = args.left[promptIndex]
    const rightPrompt = args.right[promptIndex]
    if (
      !leftPrompt ||
      !rightPrompt ||
      (args.compareIds && leftPrompt.id !== rightPrompt.id) ||
      leftPrompt.prompt !== rightPrompt.prompt ||
      leftPrompt.header !== rightPrompt.header ||
      leftPrompt.allowMultiple !== rightPrompt.allowMultiple ||
      leftPrompt.allowCustom !== rightPrompt.allowCustom ||
      leftPrompt.options.length !== rightPrompt.options.length
    ) {
      return false
    }

    for (let optionIndex = 0; optionIndex < leftPrompt.options.length; optionIndex += 1) {
      const leftOption = leftPrompt.options[optionIndex]
      const rightOption = rightPrompt.options[optionIndex]
      if (
        !leftOption ||
        !rightOption ||
        (args.compareIds && leftOption.id !== rightOption.id) ||
        leftOption.label !== rightOption.label ||
        leftOption.description !== rightOption.description
      ) {
        return false
      }
    }
  }

  return true
}

function canReuseQuestionInteraction(args: {
  existing: AgentRuntimeInteractionRequest
  next: Extract<AgentRuntimeInteractionRequest, { kind: "question" }>
}): boolean {
  const compareIds = args.next.providerRef?.agentType !== "opencode"
  const compareMessageToolLinkage = args.next.providerRef?.agentType !== "opencode"

  return (
    args.existing.kind === "question" &&
    args.existing.title === args.next.title &&
    sameInteractionProviderRef({
      left: args.existing.providerRef,
      right: args.next.providerRef,
      compareMessageToolLinkage,
    }) &&
    sameQuestionPrompts({
      left: args.existing.prompts,
      right: args.next.prompts,
      compareIds,
    })
  )
}

function canReuseAskUserInteraction(args: {
  existing: AgentRuntimeInteractionRequest
  next: Extract<AgentRuntimeInteractionRequest, { kind: "ask_user" }>
}): boolean {
  return (
    args.existing.kind === "ask_user" &&
    args.existing.title === args.next.title &&
    sameInteractionProviderRef({
      left: args.existing.providerRef,
      right: args.next.providerRef,
    }) &&
    sameQuestionPrompts({
      left: args.existing.prompts,
      right: args.next.prompts,
      compareIds: true,
    })
  )
}

export function buildPendingInteractionsFromStatus(input: {
  agentType: AgentType
  pendingQuestions: Array<Record<string, unknown>>
  pendingPermissions: Array<Record<string, unknown>>
  existingInteractions?: AgentRuntimeInteractionRequest[]
}): AgentRuntimeInteractionRequest[] {
  const existingByKey = new Map(
    (input.existingInteractions ?? []).map((interaction) => [`${interaction.kind}:${interaction.requestId}`, interaction] as const),
  )

  const interactions: AgentRuntimeInteractionRequest[] = []

  for (const question of input.pendingQuestions) {
    const requestId = asString(question.requestId) ?? asString(question.id)
    if (!requestId) continue

    if (input.agentType === "cursor") {
      if (question.kind === "plan") {
        const plan = buildPlanPrompts(question, requestId)
        const nextInteraction: Extract<AgentRuntimeInteractionRequest, { kind: "question" }> = {
          kind: "question",
          requestId,
          title: plan.title,
          prompts: plan.prompts,
          raw: question,
          providerRef: {
            agentType: "cursor",
            sessionId: asString(question.sessionId) ?? asString(question.sessionID) ?? undefined,
          },
        }
        const existing = existingByKey.get(`question:${requestId}`)
        interactions.push(
          existing && canReuseQuestionInteraction({ existing, next: nextInteraction })
            ? existing
            : nextInteraction,
        )
        continue
      }

      const nextInteraction: Extract<AgentRuntimeInteractionRequest, { kind: "question" }> = {
        kind: "question",
        requestId,
        title: asString(question.title) ?? undefined,
        prompts: normalizeQuestionPrompts(question, requestId),
        raw: question,
        providerRef: {
          agentType: "cursor",
          sessionId: asString(question.sessionId) ?? asString(question.sessionID) ?? undefined,
        },
      }
      const existing = existingByKey.get(`question:${requestId}`)
      interactions.push(
        existing && canReuseQuestionInteraction({ existing, next: nextInteraction })
          ? existing
          : nextInteraction,
      )
      continue
    }

    const rawQuestions = Array.isArray(question.questions) ? question.questions : []
    const providerTool = asRecord(question.tool)
    const nextInteraction: Extract<AgentRuntimeInteractionRequest, { kind: "question" }> = {
      kind: "question",
      requestId,
      prompts: rawQuestions.map((rawQuestion, index) => {
        const record = asRecord(rawQuestion) ?? {}
        const rawOptions = Array.isArray(record.options) ? record.options : []
        return {
          id: asString(record.header) ?? `question-${index + 1}`,
          prompt: asString(record.question) ?? "",
          header: asString(record.header) ?? undefined,
          allowMultiple: record.multiple === true,
          allowCustom: record.custom === true,
          options: rawOptions
            .map((rawOption) => {
              const option = asRecord(rawOption)
              const label = asString(option?.label)
              if (!label) return null
              return {
                label,
                description: asString(option?.description) ?? undefined,
              }
            })
            .filter((option): option is NonNullable<typeof option> => option !== null),
        }
      }),
      raw: question,
      providerRef: {
        agentType: input.agentType,
        sessionId: asString(question.sessionID) ?? undefined,
        messageId: asString(providerTool?.messageID) ?? undefined,
        toolCallId: asString(providerTool?.callID) ?? undefined,
      },
    }
    const existing = existingByKey.get(`question:${requestId}`)
    interactions.push(
      existing && canReuseQuestionInteraction({ existing, next: nextInteraction })
        ? existing
        : nextInteraction,
    )
  }

  for (const permission of input.pendingPermissions) {
    const requestId = asString(permission.id) ?? asString(permission.requestId)
    if (!requestId) continue

    const existingPermission = existingByKey.get(`permission:${requestId}`)
    if (existingPermission && existingPermission.kind === "permission") {
      interactions.push(existingPermission)
      continue
    }
    if (input.agentType === "cursor") {
      const toolCall = asRecord(permission.toolCall)
      interactions.push({
        kind: "permission",
        requestId,
        title: asString(toolCall?.title) ?? "Permission required",
        description: asString(toolCall?.kind) ?? undefined,
        toolName: asString(toolCall?.title) ?? asString(toolCall?.kind) ?? undefined,
        input: toolCall ?? permission,
        raw: permission,
        providerRef: {
          agentType: "cursor",
          sessionId: asString(permission.sessionId) ?? undefined,
          toolCallId: asString(toolCall?.toolCallId) ?? asString(toolCall?.tool_call_id) ?? undefined,
        },
      })
      continue
    }

    const providerTool = asRecord(permission.tool)
    const providerRef = {
      agentType: input.agentType,
      sessionId: asString(permission.sessionID) ?? asString(permission.sessionId) ?? undefined,
      messageId: asString(providerTool?.messageID) ?? undefined,
      toolCallId: asString(providerTool?.callID) ?? undefined,
    } as const

    const toolName = asString(permission.toolName) ?? undefined
    if (toolName === "AskQuestion" || toolName === "AskUserQuestion") {
      const askUser = normalizeClaudeAskUserInput({ input: permission.input, requestId })
      const nextInteraction: Extract<AgentRuntimeInteractionRequest, { kind: "ask_user" }> = {
        kind: "ask_user",
        requestId,
        title: askUser?.title ?? undefined,
        prompts: askUser?.questions ?? [],
        raw: permission,
        providerRef,
      }
      const existingAskUser = existingByKey.get(`ask_user:${requestId}`)
      interactions.push(
        existingAskUser && canReuseAskUserInteraction({ existing: existingAskUser, next: nextInteraction })
          ? existingAskUser
          : nextInteraction,
      )
      continue
    }

    interactions.push({
      kind: "permission",
      requestId,
      title: toolName ? `Permission required: ${toolName}` : undefined,
      description: asString(permission.permission) ?? undefined,
      permission: asString(permission.permission) ?? undefined,
      toolName,
      patterns: Array.isArray(permission.patterns)
        ? permission.patterns.filter((value): value is string => typeof value === "string")
        : undefined,
      input: permission.input,
      raw: permission,
      providerRef,
    })
  }

  return interactions
}

export function buildRuntimeStatusFromStatusPayload(input: {
  agentType: AgentType
  pendingInteractionCount: number
  agentStatus: Record<string, unknown> | null
  activityState?: string | null
  activityTransitionAt?: number | null
  lastFinishedAt?: number | null
  lastFinishReason?: string | null
  runtimeLastErrorCode?: string | null
  runtimeLastErrorMessage?: string | null
  replicationIntegrity?: Record<string, unknown> | null
  runtimeFinality?: Record<string, unknown> | null
}): AgentRuntimeStatus {
  const rawType = asString(input.agentStatus?.type)?.toLowerCase() ?? "idle"
  const activityState = asString(input.activityState)?.toLowerCase() ?? "idle"

  let phase: AgentRuntimeStatus["phase"] = "idle"
  if (
    rawType === "error" ||
    rawType === "failed" ||
    activityState === "error"
  ) {
    phase = "error"
  } else if (rawType === "waiting_input" || input.pendingInteractionCount > 0) {
    phase = "waiting_input"
  } else if (
    rawType === "busy" ||
    rawType === "running" ||
    rawType === "retry" ||
    rawType === "working" ||
    activityState === "running"
  ) {
    phase = "running"
  }

  const runtimeLastErrorCode = asString(input.runtimeLastErrorCode)
  const agentStatusType = asString(input.agentStatus?.type)
  const normalizedError = normalizeAgentErrorDetails({
    value: {
      message:
        asString(input.runtimeLastErrorMessage) ??
        asString(input.agentStatus?.message),
      code: runtimeLastErrorCode,
    },
    fallbackMessage: runtimeLastErrorCode ? `Runtime error: ${runtimeLastErrorCode}` : null,
    fallbackCode: runtimeLastErrorCode,
  })

  return {
    phase,
    agentType: input.agentType,
    startedAt:
      normalizeTimestamp(input.agentStatus?.startedAt) ??
      normalizeTimestamp(input.activityTransitionAt),
    finishedAt:
      normalizeTimestamp(input.agentStatus?.finishedAt) ??
      normalizeTimestamp(input.lastFinishedAt),
    finishReason:
      asString(input.agentStatus?.finishReason) ??
      asString(input.lastFinishReason) ??
      (phase === "waiting_input" ? "waiting_input" : phase === "idle" ? "completed" : null),
    error:
      normalizedError
        ? {
            type:
              normalizedError.code ??
              runtimeLastErrorCode ??
              normalizedError.type ??
              agentStatusType ??
              "runtime_error",
            message: normalizedError.message,
          }
        : null,
    replicationIntegrity: asReplicationIntegrity(input.replicationIntegrity),
    runtimeFinality: asRuntimeFinality(input.runtimeFinality),
  }
}
